import normalize from "json-api-normalizer";
import { get, isEmpty } from "lodash";

import {
  SET_PRODUCTS,
  SET_PRODUCT_OPTIONS,
  SET_PRODUCTS_FAILED,
  PRODUCT_SAVED,
  PRODUCT_SAVE_STARTED,
  PRODUCT_SAVE_FAILED,
  PRODUCT_UPDATE_SUCCESS,
  PRODUCT_UPDATE_FAILED,
  ASYNC_START,
  ASYNC_END,
  FETCH_CUSTOMER_AVAILABLE_PRODUCTS_START,
  FETCH_CUSTOMER_AVAILABLE_PRODUCTS_SUCCESS,
  FETCH_CUSTOMER_AVAILABLE_PRODUCTS_FAILED,
  FETCH_FILTERED_PRODUCT_BUNDLES_START,
  FETCH_FILTERED_PRODUCT_BUNDLES_SUCCESS,
  FETCH_FILTERED_PRODUCT_BUNDLES_FAILED,
  FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_START,
  FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_SUCCESS,
  FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_FAILED,
  FETCH_FILTERED_PRODUCT_NOT_ADDONS_START,
  FETCH_FILTERED_PRODUCT_NOT_ADDONS_SUCCESS,
  FETCH_FILTERED_PRODUCT_NOT_ADDONS_FAILED
} from "./types";
import ProductsService from "../services/products.service";
import NotificationService from "../services/notification.service";
import EncodingFiltersService from "../services/encoding-filters.service";

const getProducts = async (dispatch, tenantSlug, requestParams) => {
  try {
    const productResult = await ProductsService.getProducts(tenantSlug, requestParams);
    const normalizedProducts = normalize(productResult.data);
    dispatch(setProducts(normalizedProducts));
  } catch (e) {
    dispatch(setProductsFailed());
    NotificationService.addError(`Failed fetching the products! ${NotificationService.handleMessage(e)}`);
  }
};

export const initProducts = params => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  await getProducts(dispatch, tenantSlug, {
    filter: params
  });
};

export const fetchProductOptions = () => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const productResult = await ProductsService.getProducts(tenantSlug, {
      fields: { products: "name" },
      withoutIncludes: true
    });
    const normalizedProducts = normalize(productResult.data);
    dispatch(setProductOptions(normalizedProducts));
  } catch (e) {
    dispatch(setProductsFailed());
    NotificationService.addError(`Failed fetching the products! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchFilteredProductOptions = () => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const productResult = await ProductsService.filterAddOns(tenantSlug);
    const normalizedProducts = normalize(productResult.data);
    dispatch(setProductOptions(normalizedProducts));
  } catch (e) {
    dispatch(setProductsFailed());
    NotificationService.addError(`Failed fetching the products! ${NotificationService.handleMessage(e)}`);
  }
};

export const filterProducts = filter => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  try {
    const productResult = await ProductsService.filterProducts(tenantSlug, EncodingFiltersService.encode(filter));
    const normalizedProducts = productResult.data.data.length ? normalize(productResult.data) : { products: {} };
    dispatch(setProducts(normalizedProducts));
  } catch (e) {
    dispatch(setProductsFailed());
    NotificationService.addError(`Failed filtering the products! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchProduct = (slug, customerSlug = null) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const productResult = customerSlug
      ? await ProductsService.getDiscount(tenantSlug, slug, customerSlug)
      : await ProductsService.get(tenantSlug, slug);
    const normalizedProducts = normalize(productResult.data);
    dispatch(setProducts(normalizedProducts));
  } catch (e) {
    dispatch(setProductsFailed(get(e, "response.data.errors")));
    NotificationService.addError(`Failed fetching the product! ${NotificationService.handleMessage(e)}`);
  }
};

export const toggleArchivedProducts = (slug, productData) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  dispatch({ type: ASYNC_START });

  try {
    await ProductsService.toggleArchived(slug, tenantSlug, {
      product: productData
    });
    dispatch(productSaved());
    dispatch({ type: ASYNC_END });
    dispatch(
      filterProducts({
        filter: {
          archived: !productData.archived
        }
      })
    );
    NotificationService.addSuccess(`Product successfully ${productData.archived ? "archived!" : "activated!"}`);
  } catch (e) {
    dispatch(productSaveFailed());
    dispatch({ type: ASYNC_END });
    NotificationService.addError(
      `Failed ${productData.archived ? "archiving!" : "activating!"} the product! ${NotificationService.handleMessage(e)}`
    );
  }
};

export const updateProduct = (slug, data) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: PRODUCT_SAVE_STARTED });

  try {
    await ProductsService.update(slug, tenantSlug, data);

    await Promise.all(
      data.productPrices
        .filter(price => price.removed)
        .map(price => {
          return ProductsService.deletePrice(price.id, tenantSlug, price);
        })
    );

    await Promise.all(
      data.productPrices.map(price => {
        if (price.id && !price.removed) return ProductsService.updatePrice(price.id, tenantSlug, price);
        else if (!price.id && !price.removed) return ProductsService.createPrice(slug, tenantSlug, price);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.productMetadataItems
        .filter(productMetadataItem => productMetadataItem.id && productMetadataItem.removed === true)
        .map(productMetadataItem => {
          return ProductsService.removeFeatureLink(productMetadataItem.id, tenantSlug);
        })
    );

    await Promise.all(
      data.productMetadataItems.map(productMetadataItem => {
        if (productMetadataItem.id && !productMetadataItem.removed) {
          return ProductsService.updateFeatureLink(tenantSlug, productMetadataItem);
        } else if (!(productMetadataItem.id || productMetadataItem.removed))
          return ProductsService.createFeatureLink(tenantSlug, slug, productMetadataItem);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.productBundles
        .filter(productBundle => productBundle.id && productBundle.removed === true)
        .map(productBundle => {
          return ProductsService.removeBundleLink(productBundle.id, tenantSlug);
        })
    );

    await Promise.all(
      data.productBundles.map(productBundle => {
        if (productBundle.id && !productBundle.removed) {
          return ProductsService.updateBundleLink(tenantSlug, productBundle);
        } else if (!(productBundle.id || productBundle.removed))
          return ProductsService.createBundleLink(tenantSlug, slug, productBundle);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.productLicenseBundles
        .filter(productLicenseBundle => productLicenseBundle.id && productLicenseBundle.removed === true)
        .map(productLicenseBundle => {
          return ProductsService.removeLicenseBundleLink(productLicenseBundle.id, tenantSlug);
        })
    );

    await Promise.all(
      data.productLicenseBundles.map(productLicenseBundle => {
        if (productLicenseBundle.id && !productLicenseBundle.removed) {
          return ProductsService.updateLicenseBundleLink(tenantSlug, productLicenseBundle);
        } else if (!(productLicenseBundle.id || productLicenseBundle.removed))
          return ProductsService.createLicenseBundleLink(tenantSlug, slug, productLicenseBundle);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.productAddOns.map(productAddOn => {
        if (isEmpty(data.product.addOns.filter(addOn => addOn.productSlug === productAddOn))) {
          ProductsService.createProductAddOn(tenantSlug, slug, productAddOn);
        }
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.product.addOns.map(addOn => {
        if (!data.productAddOns.includes(addOn.productSlug)) {
          return ProductsService.removeProductAddOn(tenantSlug, addOn.id);
        }
        return Promise.resolve(true);
      })
    );

    dispatch(productUpdateSuccess(slug));
    NotificationService.addSuccess("Product successfully updated!");
  } catch (e) {
    dispatch(productUpdateFailed(slug, get(e, "response.data.errors")));
    NotificationService.addError(`Failed updating the product! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchCustomerAvailableProducts = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  const customerSlug = { with_discounts_for: data };
  dispatch({ type: FETCH_CUSTOMER_AVAILABLE_PRODUCTS_START });

  try {
    const result = await ProductsService.availableForCustomer(tenantSlug, data, EncodingFiltersService.encode(customerSlug));

    const normalizedProducts = normalize(result.data);

    dispatch({
      type: FETCH_CUSTOMER_AVAILABLE_PRODUCTS_SUCCESS,
      payload: normalizedProducts
    });
  } catch (e) {
    dispatch({ type: FETCH_CUSTOMER_AVAILABLE_PRODUCTS_FAILED });
    NotificationService.addError(`Failed fetching customer active product! ${NotificationService.handleMessage(e)}`);
  }
};

export const createProduct = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: PRODUCT_SAVE_STARTED });

  try {
    const createProductResponse = await ProductsService.create(tenantSlug, data);

    if (createProductResponse.status !== 201) {
      return dispatch(productSaveFailed());
    }

    const productSlug = get(createProductResponse, "data.data.id");

    const createPriceResponse = await Promise.all(
      data.productPrices
        .filter(price => !price.removed)
        .map(price => ProductsService.createPrice(productSlug, tenantSlug, price))
    );

    if (createPriceResponse.find(response => response.status !== 201)) {
      return dispatch(productSaveFailed());
    }

    const createBundlesResponse = await Promise.all(
      data.productBundles
        .filter(productBundle => !productBundle.removed)
        .map(productBundle => ProductsService.createBundleLink(tenantSlug, productSlug, productBundle))
    );

    if (createBundlesResponse.find(response => response.status !== 201)) {
      return dispatch(productSaveFailed());
    }

    const createLicenseBundlesResponse = await Promise.all(
      data.productLicenseBundles
        .filter(productLicenseBundle => !productLicenseBundle.removed)
        .map(productLicenseBundle => ProductsService.createLicenseBundleLink(tenantSlug, productSlug, productLicenseBundle))
    );

    if (createBundlesResponse.find(response => response.status !== 201)) {
      return dispatch(productSaveFailed());
    }

    const createFeaturesResponse = await Promise.all(
      data.productMetadataItems
        .filter(productMetadataItem => !productMetadataItem.removed)
        .map(productMetadataItem => ProductsService.createFeatureLink(tenantSlug, productSlug, productMetadataItem))
    );

    if (createFeaturesResponse.find(response => response.status !== 201)) {
      return dispatch(productSaveFailed());
    }

    const createProductAddOnsResponse = await Promise.all(
      data.productAddOns
        .filter(productAddOn => !productAddOn.removed)
        .map(productAddOn => ProductsService.createProductAddOn(tenantSlug, productSlug, productAddOn))
    );

    if (createProductAddOnsResponse.find(response => response.status !== 201)) {
      return dispatch(productSaveFailed());
    }

    dispatch(productSaved());
    NotificationService.addSuccess("Product successfully created!");
  } catch (e) {
    const errors = get(e, "response.data.errors");
    dispatch(productSaveFailed(errors));
    NotificationService.addError(`Failed creating the product! ${NotificationService.handleMessage(e)}`);
  }
};

export const setProducts = products => {
  return {
    type: SET_PRODUCTS,
    products: products
  };
};

const setProductOptions = products => {
  return {
    type: SET_PRODUCT_OPTIONS,
    products: products
  };
};

export const productUpdateSuccess = slug => ({
  type: PRODUCT_UPDATE_SUCCESS,
  slug
});
export const productUpdateFailed = (slug, errors) => ({
  type: PRODUCT_UPDATE_FAILED,
  slug,
  errors
});

export const setProductsFailed = errors => {
  return {
    type: SET_PRODUCTS_FAILED,
    errors
  };
};

export const productSaved = () => {
  return {
    type: PRODUCT_SAVED
  };
};

export const productSaveFailed = errors => {
  return {
    type: PRODUCT_SAVE_FAILED,
    errors
  };
};

export const filterProductBundles = productSlug => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_FILTERED_PRODUCT_BUNDLES_START });

  try {
    const params = { filter: { productSlug: productSlug }, include: "bundle" };
    const result = await ProductsService.filterProductBundles(tenantSlug, params);

    const normalizedProductBundles = normalize(result.data);

    dispatch({
      type: FETCH_FILTERED_PRODUCT_BUNDLES_SUCCESS,
      payload: normalizedProductBundles
    });
  } catch (e) {
    dispatch({ type: FETCH_FILTERED_PRODUCT_BUNDLES_FAILED });
    NotificationService.addError("Failed fetching filtered product bundles!");
  }
};

export const fetchFilteredProductBundles = ({ filter, include }) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_FILTERED_PRODUCT_BUNDLES_START });

  try {
    const params = { filter, include };
    const result = await ProductsService.filterProductBundles(tenantSlug, params);

    const payload = normalize(result.data);

    dispatch({
      type: FETCH_FILTERED_PRODUCT_BUNDLES_SUCCESS,
      payload
    });
  } catch (e) {
    dispatch({ type: FETCH_FILTERED_PRODUCT_BUNDLES_FAILED });
    NotificationService.addError("Failed fetching filtered product bundles!");
  }
};

export const filterProductLicenseBundles = productSlug => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_START });

  try {
    const params = { filter: { productSlug: productSlug }, include: "license_bundle" };
    const result = await ProductsService.filterProductLicenseBundles(tenantSlug, params);

    const normalizedProductBundles = normalize(result.data);

    dispatch({
      type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_SUCCESS,
      payload: normalizedProductBundles
    });
  } catch (e) {
    dispatch({ type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_FAILED });
    NotificationService.addError("Failed fetching filtered product license bundles!");
  }
};

export const fetchFilteredProductLicenseBundles = ({ filter, include }) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_START });

  try {
    const params = { filter, include };
    const result = await ProductsService.filterProductLicenseBundles(tenantSlug, params);

    const payload = normalize(result.data);

    dispatch({
      type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_SUCCESS,
      payload
    });
  } catch (e) {
    dispatch({ type: FETCH_FILTERED_PRODUCT_LICENSE_BUNDLES_FAILED });
    NotificationService.addError("Failed fetching filtered product license bundles!");
  }
};

export const filterProductsAddOns = () => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_FILTERED_PRODUCT_NOT_ADDONS_START });

  try {
    const productsResult = await ProductsService.filterProductsAddOns(tenantSlug);
    const normalizedProducts = normalize(productsResult.data);

    dispatch({
      type: FETCH_FILTERED_PRODUCT_NOT_ADDONS_SUCCESS,
      payload: normalizedProducts
    });
  } catch (e) {
    dispatch({ type: FETCH_FILTERED_PRODUCT_NOT_ADDONS_FAILED });
    NotificationService.addError("Failed fetching products that are not addons!");
  }
};
